import { transformIndividualResourceEntry } from 'layers/content/v3/transforms/transformIndividualResourceEntry'
import { getSectionPhase } from 'utils/individualResource'

const toLessonTitleFromSlug = slug => {
  if (!slug || typeof slug !== 'string') {
    return ''
  }

  return slug
    .split('-')
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

const getFirstTruthyValue = values => {
  return values.find(Boolean) || null
}

const PROPERTY_TO_FACET_ID = {
  resourceType: 'metadataResourceType',
  duration: 'metadataDuration',
  frequency: 'metadataFrequency',
  grade: 'metadataGrade',
  audience: 'metadataAudience',
  unit: 'metadataUnit',
  goal: 'metadataGoal',
  learningCondition: 'metadataLearningCondition',
  academicSkill: 'metadataAcademicSkills',
  caselCompetency: 'metadataCasel',
  materials: 'metadataMaterials',
  programTheme: 'metadataProgramThemes',
}

const getCanonicalFacetId = ({ facetId, facetMappings }) => {
  if (!facetId || typeof facetId !== 'string') {
    return null
  }

  const facetIdByProperty = Object.values(PROPERTY_TO_FACET_ID)
  const lowerFirstFacetId = `${facetId.charAt(0).toLowerCase()}${facetId.slice(
    1,
  )}`
  const candidates = [facetId, lowerFirstFacetId]

  return (
    candidates.find(
      candidate =>
        !!facetMappings.byFacetId[candidate] ||
        facetIdByProperty.includes(candidate),
    ) || null
  )
}

const toSnakeCase = value => {
  return String(value || '')
    .replace(/([A-Z])/g, '_$1')
    .toLowerCase()
}

const getFacetMappings = facetMetadataSetting => {
  const facets = [
    ...(facetMetadataSetting?.column1Facets || []),
    ...(facetMetadataSetting?.column2Facets || []),
  ]
  const byFacetId = {}
  const byProperty = {}

  facets.forEach(facet => {
    if (facet?.facetId && facet?.facetPropertyName) {
      byFacetId[facet.facetId] = facet.facetPropertyName
      byProperty[facet.facetPropertyName] = facet.facetId
    }
  })

  return { byFacetId, byProperty }
}

const getMetadataPropertyName = ({ metadataValue, facetId, facetMappings }) => {
  if (!metadataValue || typeof metadataValue !== 'object') {
    return ''
  }

  const mappedPropertyName = facetMappings.byFacetId[facetId]
  if (mappedPropertyName) {
    return mappedPropertyName
  }

  const ignoredKeys = new Set([
    'contentType',
    'facetId',
    'facet_id',
    'value',
    'label',
    'displayValue',
    'display_value',
    'id',
    'type',
    'locale',
  ])
  const candidatePropertyName = Object.keys(metadataValue).find(
    key => !ignoredKeys.has(key),
  )

  return candidatePropertyName || ''
}

const normalizeMetadataValue = ({ metadataValue, facetMappings }) => {
  const rawFacetId = getFirstTruthyValue([
    metadataValue?.contentType?.id,
    typeof metadataValue?.contentType === 'string'
      ? metadataValue.contentType
      : null,
    metadataValue?.facetId,
    metadataValue?.facet_id,
  ])
  const facetId = getCanonicalFacetId({
    facetId: rawFacetId,
    facetMappings,
  })

  const propertyName = getMetadataPropertyName({
    metadataValue,
    facetId,
    facetMappings,
  })
  const resolvedFacetId =
    facetId ||
    facetMappings.byProperty[propertyName] ||
    PROPERTY_TO_FACET_ID[propertyName] ||
    rawFacetId ||
    null

  if (!propertyName && !resolvedFacetId) {
    return metadataValue
  }

  const resolvedPropertyName =
    propertyName ||
    facetMappings.byFacetId[resolvedFacetId] ||
    Object.keys(PROPERTY_TO_FACET_ID).find(
      key => PROPERTY_TO_FACET_ID[key] === resolvedFacetId,
    ) ||
    'value'

  const value = getFirstTruthyValue([
    metadataValue?.[resolvedPropertyName],
    metadataValue?.[toSnakeCase(resolvedPropertyName)],
    metadataValue?.value,
    metadataValue?.displayValue,
    metadataValue?.display_value,
  ])

  if (value == null) {
    return metadataValue
  }

  return {
    ...metadataValue,
    contentType: {
      id: resolvedFacetId || metadataValue?.contentType?.id || '',
    },
    [resolvedPropertyName]: value,
  }
}

const buildMetadataValuesFromResourceMetadata = ({
  resourceMetadata,
  facetMetadataSetting,
}) => {
  const facets = [
    ...(facetMetadataSetting?.column1Facets || []),
    ...(facetMetadataSetting?.column2Facets || []),
  ]

  return facets
    .map(facet => {
      const value = getFirstTruthyValue([
        resourceMetadata?.[facet.facetPropertyName],
        resourceMetadata?.[toSnakeCase(facet.facetPropertyName)],
      ])

      if (value == null) {
        return null
      }

      return {
        contentType: {
          id: facet.facetId,
        },
        [facet.facetPropertyName]: value,
      }
    })
    .filter(Boolean)
}

const normalizeResourceMetadata = ({ entry, resourceMetadata }) => {
  const facetMetadataSetting =
    entry?.facetMetadataSetting || entry?.facet_metadata_setting || {}
  const facetMappings = getFacetMappings(facetMetadataSetting)
  const routeSlug = getFirstTruthyValue([
    resourceMetadata?.routeSlug,
    resourceMetadata?.route_slug,
    entry?.routeSlug,
    entry?.route_slug,
    entry?.slug,
  ])

  const displayTitle =
    getFirstTruthyValue([
      resourceMetadata?.displayTitle,
      resourceMetadata?.display_title,
      entry?.displayTitle,
      entry?.display_title,
      entry?.title,
      entry?.internalTitle,
    ]) || toLessonTitleFromSlug(routeSlug)

  const description = getFirstTruthyValue([
    resourceMetadata?.description,
    entry?.description,
    entry?.summary,
  ])

  const rawMetadataValues =
    getFirstTruthyValue([
      Array.isArray(resourceMetadata?.metadataValues) &&
      resourceMetadata.metadataValues.length
        ? resourceMetadata.metadataValues
        : null,
      Array.isArray(resourceMetadata?.metadata_values) &&
      resourceMetadata.metadata_values.length
        ? resourceMetadata.metadata_values
        : null,
      Array.isArray(entry?.metadataValues) && entry.metadataValues.length
        ? entry.metadataValues
        : null,
      Array.isArray(entry?.metadata_values) && entry.metadata_values.length
        ? entry.metadata_values
        : null,
      Array.isArray(entry?.metadata) && entry.metadata.length
        ? entry.metadata
        : null,
    ]) || []
  const normalizedMetadataValues = rawMetadataValues
    .map(metadataValue =>
      normalizeMetadataValue({ metadataValue, facetMappings }),
    )
    .filter(Boolean)
  const fallbackMetadataValues = buildMetadataValuesFromResourceMetadata({
    resourceMetadata,
    facetMetadataSetting,
  })
  const metadataValues = normalizedMetadataValues.length
    ? normalizedMetadataValues
    : fallbackMetadataValues

  return {
    ...resourceMetadata,
    routeSlug,
    displayTitle,
    description,
    metadataValues,
    courseNodeConnector:
      resourceMetadata?.courseNodeConnector ||
      resourceMetadata?.course_node_connector ||
      entry?.courseNodeConnector ||
      {},
  }
}

const getSectionDisplayLabel = section => {
  const labelCandidate = getFirstTruthyValue([
    section?.displayTitle,
    section?.display_title,
    section?.title,
    section?.sectionTitle,
    section?.section_title,
    section?.h2,
    section?.h3,
  ])

  if (typeof labelCandidate !== 'string') {
    return null
  }

  const trimmedLabel = labelCandidate.trim()
  return trimmedLabel.length ? trimmedLabel : null
}

const getContentBlockWithMarkerHeadingRemoved = ({
  contentBlock,
  markerPhase,
}) => {
  if (!contentBlock || markerPhase == null) {
    return null
  }

  const markerFieldCandidates = [
    'sectionType',
    'phase',
    'type',
    'h2',
    'h3',
    'displayTitle',
    'title',
  ]
  const markerFields = markerFieldCandidates.filter(fieldName => {
    if (!contentBlock[fieldName]) {
      return false
    }

    return (
      getSectionPhase({
        [fieldName]: contentBlock[fieldName],
      }) === markerPhase
    )
  })

  if (!markerFields.length) {
    return contentBlock
  }

  const normalizedContentBlock = {
    ...contentBlock,
  }
  markerFields.forEach(fieldName => {
    normalizedContentBlock[fieldName] = ''
  })

  const hasRenderableValue = Object.entries(normalizedContentBlock).some(
    ([key, value]) => {
      if (
        key === 'contentType' ||
        key === '__typename' ||
        key === 'sectionType' ||
        key === 'phase' ||
        key === 'type'
      ) {
        return false
      }

      return String(value || '').trim().length > 0
    },
  )

  return hasRenderableValue ? normalizedContentBlock : null
}

const normalizeSimpleContentBlock = contentBlock => {
  if (!contentBlock || typeof contentBlock !== 'object') {
    return null
  }

  if (contentBlock.contentType || contentBlock.__typename) {
    return contentBlock
  }

  const hasSimpleCopy =
    !!contentBlock.h2 ||
    !!contentBlock.h3 ||
    !!contentBlock.bodyCopy ||
    !!contentBlock.text

  if (!hasSimpleCopy) {
    return contentBlock
  }

  return {
    ...contentBlock,
    contentType: {
      id: 'headerBodyCopy',
    },
    bodyCopy: contentBlock.bodyCopy || contentBlock.text || '',
    h2: contentBlock.h2 || '',
    h3: contentBlock.h3 || '',
  }
}

const getContentTypeId = contentBlock => {
  const rawType =
    contentBlock?.contentType ||
    contentBlock?.content_type ||
    contentBlock?.__typename ||
    ''

  if (typeof rawType === 'string') {
    return rawType.toLowerCase()
  }

  return String(rawType?.id || '').toLowerCase()
}

const getAssetTypeId = asset => {
  if (!asset || typeof asset !== 'object') {
    return ''
  }

  const rawType =
    asset.contentType || asset.content_type || asset.__typename || ''
  if (typeof rawType === 'string') {
    return rawType.toLowerCase()
  }

  return String(rawType?.id || '').toLowerCase()
}

const getAssetUrl = asset => {
  if (!asset || typeof asset !== 'object') {
    return ''
  }

  return (
    asset.url || asset.href || asset.file?.url || asset.file?.file?.url || ''
  )
}

const getAssetTitle = asset => {
  if (!asset || typeof asset !== 'object') {
    return ''
  }

  return asset.displayTitle || asset.display_title || asset.description || ''
}

const getFirstMarkdownLink = markdown => {
  if (!markdown || typeof markdown !== 'string') {
    return null
  }

  const markdownLinkMatch = markdown.match(
    /\[([^\]]+)\]\(([^)\s]+)(?:\s+"[^"]*")?\)/,
  )
  if (!markdownLinkMatch) {
    return null
  }

  const [, text, href] = markdownLinkMatch
  if (!text || !href) {
    return null
  }

  return {
    href,
    text,
  }
}

const hydrateMissingUrlTextLinkIcons = ({ contentBlocks = [] }) => {
  return contentBlocks.map((contentBlock, contentIndex) => {
    if (getContentTypeId(contentBlock) !== 'textlinkicon') {
      return contentBlock
    }

    const asset = contentBlock?.asset
    if (getAssetTypeId(asset) !== 'url') {
      return contentBlock
    }

    if (getAssetUrl(asset) && getAssetTitle(asset)) {
      return contentBlock
    }

    const lookAheadBlocks = contentBlocks.slice(
      contentIndex + 1,
      contentIndex + 4,
    )
    const markdownLink = lookAheadBlocks
      .map(nextContentBlock =>
        getFirstMarkdownLink(
          nextContentBlock?.bodyCopy ||
            nextContentBlock?.body_copy ||
            nextContentBlock?.text ||
            '',
        ),
      )
      .find(Boolean)

    if (!markdownLink) {
      return contentBlock
    }

    return {
      ...contentBlock,
      asset: {
        ...(asset || {}),
        displayTitle: getAssetTitle(asset) || markdownLink.text,
        display_title: asset?.display_title || markdownLink.text,
        url: getAssetUrl(asset) || markdownLink.href,
      },
    }
  })
}

const normalizePhaseSectionContent = section => {
  if (!section || typeof section !== 'object') {
    return section
  }

  const normalizedContent = Array.isArray(section.content)
    ? hydrateMissingUrlTextLinkIcons({
        contentBlocks: section.content
          .map(normalizeSimpleContentBlock)
          .filter(Boolean),
      })
    : section.content

  return {
    ...section,
    content: normalizedContent,
  }
}

const getPhaseSectionsFromSections = sections => {
  const phaseSections = {
    beforeSection: null,
    duringSection: null,
    afterSection: null,
  }

  if (!Array.isArray(sections)) {
    return phaseSections
  }

  sections.forEach(section => {
    const phase = getSectionPhase(section)
    if (!phase) {
      return
    }

    if (phase === 'before' && !phaseSections.beforeSection) {
      phaseSections.beforeSection = section
    }
    if (phase === 'during' && !phaseSections.duringSection) {
      phaseSections.duringSection = section
    }
    if (phase === 'after' && !phaseSections.afterSection) {
      phaseSections.afterSection = section
    }
  })

  return phaseSections
}

const getPhaseSectionsFromActivityContent = activityDetails => {
  const defaultPhaseSections = {
    beforeSection: null,
    duringSection: null,
    afterSection: null,
  }
  const firstActivityWithContent = Array.isArray(activityDetails)
    ? activityDetails.find(activity => Array.isArray(activity?.content))
    : null

  if (!firstActivityWithContent) {
    return defaultPhaseSections
  }

  const phaseContentBlocks = {
    before: [],
    during: [],
    after: [],
  }
  const phaseDisplayTitles = {
    before: null,
    during: null,
    after: null,
  }
  const normalizedContentBlocks = firstActivityWithContent.content
    .map(normalizeSimpleContentBlock)
    .filter(Boolean)
  const hydratedContentBlocks = hydrateMissingUrlTextLinkIcons({
    contentBlocks: normalizedContentBlocks,
  })
  const hasAnyPhaseMarker = normalizedContentBlocks.some(
    contentBlock => !!getSectionPhase(contentBlock),
  )

  if (!hasAnyPhaseMarker) {
    return {
      beforeSection: hydratedContentBlocks.length
        ? {
            displayTitle: null,
            content: hydratedContentBlocks,
          }
        : null,
      duringSection: null,
      afterSection: null,
    }
  }

  let activePhase = 'before'

  hydratedContentBlocks.forEach(contentBlock => {
    const markerPhase = getSectionPhase(contentBlock)
    if (markerPhase) {
      activePhase = markerPhase
      if (!phaseDisplayTitles[markerPhase]) {
        phaseDisplayTitles[markerPhase] = getSectionDisplayLabel(contentBlock)
      }
      const phaseMarkerContentBlock = getContentBlockWithMarkerHeadingRemoved({
        contentBlock,
        markerPhase,
      })
      if (phaseMarkerContentBlock) {
        phaseContentBlocks[activePhase].push(phaseMarkerContentBlock)
      }
      return
    }

    if (activePhase) {
      phaseContentBlocks[activePhase].push(contentBlock)
    }
  })

  return {
    beforeSection: phaseContentBlocks.before.length
      ? {
          displayTitle: phaseDisplayTitles.before,
          content: phaseContentBlocks.before,
        }
      : null,
    duringSection: phaseContentBlocks.during.length
      ? {
          displayTitle: phaseDisplayTitles.during,
          content: phaseContentBlocks.during,
        }
      : null,
    afterSection: phaseContentBlocks.after.length
      ? {
          displayTitle: phaseDisplayTitles.after,
          content: phaseContentBlocks.after,
        }
      : null,
  }
}

const normalizeLegacyEntry = entry => {
  const resolvedResourceMetadata =
    entry?.resourceMetadata || entry?.resource_metadata || {}
  const resourceMetadata = normalizeResourceMetadata({
    entry,
    resourceMetadata: resolvedResourceMetadata,
  })
  const rawActivityDetails = getFirstTruthyValue([
    Array.isArray(entry?.activityDetails) ? entry.activityDetails : null,
    Array.isArray(entry?.activity_details) ? entry.activity_details : null,
  ])
  const sections = getFirstTruthyValue([
    Array.isArray(entry?.sections) ? entry.sections : null,
    Array.isArray(entry?.weeklyPlanSections) ? entry.weeklyPlanSections : null,
    Array.isArray(entry?.weekly_plan_sections)
      ? entry.weekly_plan_sections
      : null,
    Array.isArray(resolvedResourceMetadata?.sections)
      ? resolvedResourceMetadata.sections
      : null,
  ])
  const phaseSectionsFromSections = getPhaseSectionsFromSections(sections)
  const normalizedActivityDetails = Array.isArray(rawActivityDetails)
    ? rawActivityDetails.map(activityDetail => {
        return {
          ...activityDetail,
          displayTitle: getFirstTruthyValue([
            activityDetail?.displayTitle,
            activityDetail?.display_title,
            activityDetail?.title,
          ]),
          presentation: getFirstTruthyValue([
            activityDetail?.presentation,
            activityDetail?.presentation_data,
          ]),
          markComplete: getFirstTruthyValue([
            activityDetail?.markComplete,
            activityDetail?.mark_complete,
          ]),
          content: getFirstTruthyValue([
            Array.isArray(activityDetail?.content)
              ? activityDetail.content
              : null,
            Array.isArray(activityDetail?.activityContent)
              ? activityDetail.activityContent
              : null,
            Array.isArray(activityDetail?.activity_content)
              ? activityDetail.activity_content
              : null,
          ]),
        }
      })
    : null
  const phaseSectionsFromActivityContent = getPhaseSectionsFromActivityContent(
    normalizedActivityDetails || rawActivityDetails,
  )
  const normalizedEntry = {
    ...entry,
    displayTitle:
      entry?.displayTitle ||
      entry?.display_title ||
      resourceMetadata.displayTitle,
    description:
      entry?.description || entry?.summary || resourceMetadata.description,
    routeSlug:
      entry?.routeSlug || entry?.route_slug || resourceMetadata.routeSlug,
    activityDetails: normalizedActivityDetails,
    presentation: getFirstTruthyValue([
      entry?.presentation,
      entry?.presentation_data,
      resourceMetadata?.presentation,
      resourceMetadata?.presentation_data,
      entry?.metadata?.presentation,
      entry?.metadata?.presentation_data,
    ]),
    markComplete: getFirstTruthyValue([
      entry?.markComplete,
      entry?.mark_complete,
      resourceMetadata?.markComplete,
      resourceMetadata?.mark_complete,
      entry?.metadata?.markComplete,
      entry?.metadata?.mark_complete,
    ]),
    surveyLink: getFirstTruthyValue([entry?.surveyLink, entry?.survey_link]),
    beforeSection: normalizePhaseSectionContent(
      getFirstTruthyValue([
        entry?.beforeSection,
        entry?.before_section,
        resolvedResourceMetadata?.beforeSection,
        resolvedResourceMetadata?.before_section,
        phaseSectionsFromSections.beforeSection,
        phaseSectionsFromActivityContent.beforeSection,
      ]),
    ),
    duringSection: normalizePhaseSectionContent(
      getFirstTruthyValue([
        entry?.duringSection,
        entry?.during_section,
        resolvedResourceMetadata?.duringSection,
        resolvedResourceMetadata?.during_section,
        phaseSectionsFromSections.duringSection,
        phaseSectionsFromActivityContent.duringSection,
      ]),
    ),
    afterSection: normalizePhaseSectionContent(
      getFirstTruthyValue([
        entry?.afterSection,
        entry?.after_section,
        resolvedResourceMetadata?.afterSection,
        resolvedResourceMetadata?.after_section,
        phaseSectionsFromSections.afterSection,
        phaseSectionsFromActivityContent.afterSection,
      ]),
    ),
    facetMetadataSetting:
      entry?.facetMetadataSetting || entry?.facet_metadata_setting,
    resourceMetadata,
  }

  const hasNonEmptyCanonicalActivityDetails =
    Array.isArray(normalizedEntry.activityDetails) &&
    normalizedEntry.activityDetails.length > 0
  const hasNonEmptySnakeCaseActivityDetails =
    Array.isArray(normalizedEntry.activity_details) &&
    normalizedEntry.activity_details.length > 0

  if (
    !hasNonEmptyCanonicalActivityDetails &&
    hasNonEmptySnakeCaseActivityDetails
  ) {
    normalizedEntry.activityDetails = normalizedEntry.activity_details
  }

  // Allow v3 transformer fallbacks to sections/content when legacy payload
  // includes an explicitly empty activityDetails array.
  if (
    Array.isArray(normalizedEntry.activityDetails) &&
    normalizedEntry.activityDetails.length === 0
  ) {
    delete normalizedEntry.activityDetails
  }

  return normalizedEntry
}

export const transformLegacyEntryForV2_5View = ({ entry }) => {
  if (!entry) {
    return null
  }

  return transformIndividualResourceEntry(normalizeLegacyEntry(entry))
}
